import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  SwapHoriz as SwapIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { SwapAuditEntry } from '../types';

interface SwapAuditTrailDisplayProps {
 auditTrail: SwapAuditEntry;
  compact?: boolean; // Compact view for summaries
}

const SwapAuditTrailDisplay: React.FC<SwapAuditTrailDisplayProps> = ({ 
  auditTrail,
  compact = false 
}) => {
  const { 
    originalAssignments, 
    detectedRepeats, 
    swapsPerformed, 
    finalAssignments,
    playerChanges = [],
    remainingConflicts = [],
    reason,
    timestamp 
  } = auditTrail;

  const hasConflicts = detectedRepeats.length > 0;
  const hasSwaps = swapsPerformed.length > 0;

  // Compact view - just show summary
  if (compact) {
    return (
      <Alert 
        severity={hasSwaps ? "info" : hasConflicts ? "warning" : "success"}
        icon={hasSwaps ? <SwapIcon /> : hasConflicts ? <WarningIcon /> : <CheckIcon />}
        sx={{ mb: 2 }}
      >
        <strong>Partnership Transparency:</strong> {reason}
        {hasSwaps && ` (${swapsPerformed.length} swap${swapsPerformed.length > 1 ? 's' : ''})`}
      </Alert>
    );
  }

  // Full view - show all details
  return (
    <Accordion sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center">
          <InfoIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Partnership Assignment Transparency</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>

        <Alert 
          severity={hasSwaps ? "info" : hasConflicts ? "warning" : "success"}
          sx={{ mb: 3 }}
        >
          {reason}
        </Alert>

        {/* Summary Stats */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <Chip 
            label={`Original Random: ${originalAssignments.length} players`}
            color="default"
            variant="outlined"
          />
          {hasConflicts && (
            <Chip 
              label={`${detectedRepeats.length} repeat${detectedRepeats.length > 1 ? 's' : ''} detected`}
              color="warning"
              variant="outlined"
            />
          )}
          {playerChanges.length > 0 && (
            <Chip 
              label={`${playerChanges.length} player${playerChanges.length > 1 ? 's' : ''} changed`}
              color="info"
              variant="outlined"
            />
          )}
          {hasConflicts && (
            <Chip 
              label={`${detectedRepeats.length - remainingConflicts.length} resolved`}
              color="success"
              variant="outlined"
            />
          )}
          {remainingConflicts.length > 0 && (
            <Chip 
              label={`${remainingConflicts.length} unavoidable`}
              color="error"
              variant="outlined"
            />
          )}
        </Box>

        {/* Detailed Information */}
        <Box>
          <Box>
            {/* Step 1: Original Random Assignment */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom color="primary">
                Step 1: Pure Random Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Players were randomly shuffled and assigned numbers 1-{originalAssignments.length}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Number</strong></TableCell>
                      <TableCell><strong>Player</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {originalAssignments
                      .sort((a, b) => a.assignedNumber - b.assignedNumber)
                      .map((assignment) => (
                        <TableRow key={assignment.playerId}>
                          <TableCell>#{assignment.assignedNumber}</TableCell>
                          <TableCell>{assignment.playerName}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Step 2: Conflict Detection */}
            {hasConflicts && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom color="warning.main">
                    Step 2: Recent Partnership Detection
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Found {detectedRepeats.length} partnership{detectedRepeats.length > 1 ? 's' : ''} that recently played together:
                  </Typography>
                  {detectedRepeats.map((conflict, index) => (
                    <Alert severity="warning" key={index} sx={{ mb: 1 }}>
                      <strong>{conflict.player1Name}</strong> & <strong>{conflict.player2Name}</strong> 
                      {' '}played together <strong>{conflict.frequency}x</strong> (most recent: <strong>{conflict.daysAgo} day{conflict.daysAgo !== 1 ? 's' : ''} ago</strong>)
                    </Alert>
                  ))}
                </Box>
              </>
            )}

            {/* Step 3: Number Changes Summary */}
            {playerChanges.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom color="info.main">
                    Step 3: Smart Swaps to Avoid Repeats
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Changed {playerChanges.length} player number{playerChanges.length > 1 ? 's' : ''} to resolve repeat partnerships:
                  </Typography>
                  
                  {playerChanges.map((change, index) => (
                    <Card key={change.playerId} variant="outlined" sx={{ mb: 2, backgroundColor: '#e3f2fd' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {change.playerName}
                          </Typography>
                          <Chip 
                            label={`#${change.originalNumber} → #${change.finalNumber}`}
                            color="info"
                            size="small"
                            icon={<SwapIcon />}
                          />
                        </Box>
                        {change.resolvedConflicts.length > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            ✓ No longer paired with: {change.resolvedConflicts.join(', ')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </>
            )}

            {/* Remaining Unresolved Conflicts */}
            {remainingConflicts.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box mb={3}>
                  <Typography variant="subtitle2" gutterBottom color="error.main">
                    ⚠️ Unavoidable Repeat Partnerships
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {remainingConflicts.length} repeat partnership{remainingConflicts.length > 1 ? 's' : ''} could not be avoided (no valid swap found):
                  </Typography>
                  {remainingConflicts.map((conflict, index) => (
                    <Alert severity="error" key={index} sx={{ mb: 1 }}>
                      <strong>{conflict.player1Name}</strong> & <strong>{conflict.player2Name}</strong> 
                      {' '}played together <strong>{conflict.frequency}x</strong> (most recent: <strong>{conflict.daysAgo} day{conflict.daysAgo !== 1 ? 's' : ''} ago</strong>)
                    </Alert>
                  ))}
                </Box>
              </>
            )}

            {/* Step 4: Final Assignment */}
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="subtitle2" gutterBottom color="success.main">
                Step 4: Final Assignment Used
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                {playerChanges.length > 0 ? 'After swaps, these are the final numbers used for matches:' : 'No swaps needed - original random assignment used:'}
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Number</strong></TableCell>
                      <TableCell><strong>Player</strong></TableCell>
                      {playerChanges.length > 0 && <TableCell><strong>Change</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {finalAssignments
                      .sort((a, b) => a.assignedNumber - b.assignedNumber)
                      .map((assignment) => {
                        const original = originalAssignments.find(a => a.playerId === assignment.playerId);
                        const changed = original && original.assignedNumber !== assignment.assignedNumber;
                        
                        return (
                          <TableRow 
                            key={assignment.playerId}
                            sx={changed ? { backgroundColor: '#e3f2fd' } : undefined}
                          >
                            <TableCell>#{assignment.assignedNumber}</TableCell>
                            <TableCell>{assignment.playerName}</TableCell>
                            {playerChanges.length > 0 && (
                              <TableCell>
                                {changed ? (
                                  <Chip 
                                    label={`Was #${original?.assignedNumber}`}
                                    size="small"
                                    color="info"
                                  />
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    No change
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Generated:{" "}
            {(() => {
              // Firestore timestamps arrive as { _seconds, _nanoseconds } over JSON
              const raw: any = timestamp;
              const seconds = raw?._seconds ?? raw?.seconds;
              const date =
                typeof seconds === "number"
                  ? new Date(seconds * 1000)
                  : new Date(raw);
              return isNaN(date.getTime()) ? "Unknown" : date.toLocaleString();
            })()}
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SwapAuditTrailDisplay;
